import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnInit,
  Signal,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Address } from '../../../core/models/address.model';
import { OutcomeKind } from '../../../core/models/interaction.model';
import { COME_BACK_LATER_OBJECTION_ID, Objection } from '../../../core/models/objection.model';
import { parseDefaultTagIds } from '../../../core/models/work-context.model';
import { AddressService } from '../../../core/services/address.service';
import { InteractionService } from '../../../core/services/interaction.service';
import { LoggerService } from '../../../core/services/logger.service';
import { ObjectionService } from '../../../core/services/objection.service';
import { TagService } from '../../../core/services/tag.service';
import { TranslationService } from '../../../core/services/translation.service';
import { UserStorageService } from '../../../core/services/user-storage.service';
import { WorkContextService } from '../../../core/services/work-context.service';
import { ACTIVE_WORK_CONTEXT_KEY } from '../../my-day/active-work-context';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { DncConfirmDialogComponent } from '../../../shared/components/dnc-confirm-dialog/dnc-confirm-dialog.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { ActionSheetHost } from '../action-sheet/action-sheet.component';

/** Minimum rendered host height (px, 32rem @ 16px root) for the objection subview to show
 *  notes inline instead of behind the slide-in pane -- see notesInline below. */
const NOTES_INLINE_MIN_HEIGHT_PX = 512;

interface TileModel {
  outcome_kind: OutcomeKind | '';
}

interface DetailModel {
  objection_id: string;
  follow_up_at: string; // datetime-local string; converted to epoch ms on save
}

interface NoteModel {
  notes: string;
}

interface DncModel {
  dnc: boolean;
}

interface LegacyModel {
  notes: string;
}

/**
 * InteractionComponent — the interaction screen, routed at
 * `map/addresses/:id/interactions/new` (create) and
 * `map/addresses/:id/interactions/:interactionId` (edit), with `objection` as a
 * sibling child route reached by tapping the Objection tile ("Lead" escalates
 * to the sibling `lead` route, rendering LeadWizardComponent instead) — see the
 * Canvas Route Map review.
 *
 * The Do Not Call toggle is deliberately independent of outcome — see the
 * Record Interaction redesign plan's confirmed decision #1.
 */
@Component({
  selector: 'app-interaction',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    ButtonComponent,
    DncConfirmDialogComponent,
    IconComponent,
    SpinnerComponent,
    HasPermissionDirective,
    TranslatePipe,
  ],
  templateUrl: './interaction.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './interaction.component.css',
})
export class InteractionComponent implements OnInit, AfterViewInit, ActionSheetHost {
  @ViewChild('outcomeTileTpl') private outcomeTileTpl!: TemplateRef<{ $implicit: { value: OutcomeKind | '' }; selected: boolean }>;
  @ViewChild('objectionChipTpl') private objectionChipTpl!: TemplateRef<{ $implicit: Objection; selected: boolean }>;

  private addressService = inject(AddressService);
  private interactionService = inject(InteractionService);
  private objectionService = inject(ObjectionService);
  private tagService = inject(TagService);
  private workContextService = inject(WorkContextService);
  private userStorage = inject(UserStorageService);
  private translate = inject(TranslationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private injector = inject(Injector);
  private elementRef = inject(ElementRef<HTMLElement>);
  private log = inject(LoggerService).getLogger('interaction');

  addressId = '';

  address!: Signal<Address | null>;
  addressLoading!: Signal<boolean>;

  loading = signal(true);
  saving = signal(false);
  saved = signal(false);
  error = signal<string | null>(null);
  showMenu = signal(false);
  isEditMode = signal(false);
  /** Set only in edit mode, when the loaded interaction's outcome predates the current tile set. */
  legacyOutcome = signal<OutcomeKind | null>(null);

  /** Non-null while the address is flagged do-not-contact and the rep hasn't
   *  confirmed past the gate yet -- see the Record Interaction redesign plan's
   *  confirmed decision #5 (address-level, outcome-independent confirm). */
  pendingDncConfirm = signal(false);
  private dncGateConfirmed = false;

  currentOutcome = signal<OutcomeKind | ''>('');
  objectionId = signal<string>('');
  /** Whether the notes sub-pane is slid into view in place of the objection/follow-up
   *  fields -- see the doc comment above buildNoteFields for why notes gets its own pane. */
  noteViewOpen = signal(false);
  /** Whether there's enough room to show notes inline instead of behind the slide-in pane --
   *  measured once, after view init, off this component's own rendered host height (see
   *  ngAfterViewInit). Not a live/reactive check: the action sheet's mobile "peek" height is
   *  itself now capped below NOTES_INLINE_MIN_HEIGHT_PX (see action-sheet.component.css), so
   *  peek always resolves to false here regardless of when it's measured; "full" mobile and
   *  the desktop docked panel are both a fixed height that doesn't depend on this value, so
   *  there's no circularity in measuring once rather than watching continuously. */
  notesInline = signal(false);
  /** Set once the DNC toggle is flipped on -- see onDncToggle. Flagging an
   *  address do-not-contact is terminal: there is nothing left to log here,
   *  so the sheet closes rather than leaving outcome tiles/Lead still tappable. */
  dncFlaggedClosing = signal(false);

  isTiles = computed(
    () => this.currentOutcome() === '' && !this.saved() && !this.dncFlaggedClosing() && !this.legacyOutcome() && !this.pendingDncConfirm(),
  );
  isSubview = computed(
    () => this.currentOutcome() !== '' && !this.saved() && !this.dncFlaggedClosing() && !this.legacyOutcome() && !this.pendingDncConfirm(),
  );
  canSave = computed(() => {
    const outcome = this.currentOutcome();
    if (outcome === 'no_answer') return true;
    if (outcome === 'objection') return !!this.objectionId();
    return false;
  });

  tileForm = new FormGroup({});
  tileModel: TileModel = { outcome_kind: '' };
  tileFields: FormlyFieldConfig[] = [];

  dncForm = new FormGroup({});
  dncModel: DncModel = { dnc: false };
  dncFields: FormlyFieldConfig[] = [];

  detailForm = new FormGroup({});
  detailModel: DetailModel = { objection_id: '', follow_up_at: '' };
  detailFields: FormlyFieldConfig[] = [];

  noteForm = new FormGroup({});
  noteModel: NoteModel = { notes: '' };
  noteFields: FormlyFieldConfig[] = [];

  legacyForm = new FormGroup({});
  legacyModel: LegacyModel = { notes: '' };
  legacyFields: FormlyFieldConfig[] = [];

  private objections: Objection[] = [];
  private interactionType = 'door_knock';
  private tagIds: string[] = [];
  private editingInteractionId: string | null = null;
  /** Guards the dncForm→setDoNotContact effect from firing on the initial model sync. */
  private dncInitialized = false;

  async ngOnInit(): Promise<void> {
    this.addressId = this.route.snapshot.paramMap.get('id') ?? '';
    const interactionIdParam = this.route.snapshot.paramMap.get('interactionId');
    const urlSegment = this.route.snapshot.url.at(-1)?.path ?? '';

    const addressQuery = this.addressService.createQueryById(this.addressId);
    this.address = addressQuery.data;
    this.addressLoading = addressQuery.loading;

    effect(
      () => {
        const address = this.address();
        if (!address) return;
        if (!this.dncInitialized) {
          this.dncModel.dnc = !!address.do_not_contact;
          this.dncInitialized = true;
        }
        if (address.do_not_contact && !this.dncGateConfirmed) {
          this.pendingDncConfirm.set(true);
        }
      },
      { injector: this.injector },
    );

    // Edit mode must include inactive objections -- otherwise a historic
    // interaction referencing one that's since been deactivated renders with
    // no tile selected, as if the choice were silently lost (getAll() exists
    // on ObjectionService specifically for this "rendering historical rows"
    // case). Create mode still offers only currently-valid choices.
    this.objections = interactionIdParam ? await this.objectionService.getAll() : await this.objectionService.getActive();
    // ngAfterViewInit runs synchronously right after ngOnInit is invoked, i.e.
    // before this await resolves, so its buildDetailFields() call captures
    // objections while still empty -- rebuild now that they're loaded.
    this.buildDetailFields();
    this.buildNoteFields();

    if (interactionIdParam) {
      this.isEditMode.set(true);
      this.editingInteractionId = interactionIdParam;
      await this.loadInteraction(interactionIdParam);
    } else {
      await this.applyActiveWorkContextDefaults();
      if (urlSegment === 'objection') {
        this.tileModel.outcome_kind = 'objection';
        this.currentOutcome.set('objection');
      }
    }

    this.loading.set(false);

    this.tileForm.valueChanges.subscribe(() => this.onTileSelected());
    this.detailForm.valueChanges.subscribe(() => this.objectionId.set(this.detailModel.objection_id));
    this.dncForm.valueChanges.subscribe(() => this.onDncToggle());
  }

  ngAfterViewInit(): void {
    this.tileFields = [
      {
        key: 'outcome_kind',
        type: 'card-select',
        props: {
          options: [{ value: 'no_answer' }, { value: 'objection' }],
          valueKey: 'value',
          cardTemplate: this.outcomeTileTpl,
          columns: 2,
        },
      },
    ];

    this.dncFields = [
      {
        key: 'dnc',
        type: 'toggle',
        props: { label: this.translate.translate('interactions.record.dncToggleLabel'), attributes: { 'data-testid': 'interaction-dnc-toggle' } },
      },
    ];

    this.buildDetailFields();
    this.buildNoteFields();
    this.buildLegacyFields();

    const height = this.elementRef.nativeElement.getBoundingClientRect().height;
    this.notesInline.set(height >= NOTES_INLINE_MIN_HEIGHT_PX);
  }

  private buildDetailFields(): void {
    const t = (key: string) => this.translate.translate(key);
    this.detailFields = [
      {
        key: 'objection_id',
        type: 'card-select',
        props: { options: this.objections, valueKey: 'id', cardTemplate: this.objectionChipTpl, columns: 3, compact: true },
        expressions: { hide: () => this.currentOutcome() !== 'objection' },
      },
      {
        key: 'follow_up_at',
        type: 'datetime',
        props: { label: t('interactions.record.followUpLabel'), attributes: { 'data-testid': 'interaction-followup-date' } },
        expressions: { hide: (field) => field.model?.objection_id !== COME_BACK_LATER_OBJECTION_ID },
      },
    ];
  }

  /** Notes gets its own Formly form/model/fields (rather than living in detailFields)
   *  so it can render in its own slide-in pane -- see noteViewOpen -- independently of
   *  the objection/follow-up pane, without either pane tearing down the other's controls. */
  private buildNoteFields(): void {
    const t = (key: string) => this.translate.translate(key);
    this.noteFields = [
      {
        key: 'notes',
        type: 'textarea',
        props: { label: t('interactions.record.notesLabel'), attributes: { 'data-testid': 'interaction-notes-input' } },
      },
    ];
  }

  private buildLegacyFields(): void {
    this.legacyFields = [
      {
        key: 'notes',
        type: 'textarea',
        props: { label: this.translate.translate('interactions.record.legacyOutcome.notesLabel') },
      },
    ];
  }

  private async applyActiveWorkContextDefaults(): Promise<void> {
    const activeId = this.userStorage.getTypedItem<string>(ACTIVE_WORK_CONTEXT_KEY);
    if (!activeId) return;
    const contexts = await this.workContextService.getActive();
    const context = contexts.find((c) => c.id === activeId);
    if (!context) return;

    if (context.default_interaction_type) this.interactionType = context.default_interaction_type;
    this.tagIds = parseDefaultTagIds(context);
  }

  private async loadInteraction(id: string): Promise<void> {
    try {
      const interaction = await this.interactionService.getById(id);
      if (!interaction) {
        this.error.set(this.translate.translate('interactions.form.notFoundError'));
        return;
      }
      this.interactionType = interaction.interaction_type;

      if (interaction.outcome_kind === 'no_answer' || interaction.outcome_kind === 'objection') {
        this.tileModel.outcome_kind = interaction.outcome_kind;
        this.currentOutcome.set(interaction.outcome_kind);
        this.detailModel = {
          objection_id: interaction.objection_id,
          follow_up_at: this.toLocalInput(interaction.follow_up_at),
        };
        this.noteModel = { notes: interaction.notes };
        this.objectionId.set(interaction.objection_id);
      } else {
        this.legacyOutcome.set(interaction.outcome_kind);
        this.legacyModel = { notes: interaction.notes };
      }
    } catch (err) {
      this.log.error('Failed to load interaction', err);
      this.error.set(this.translate.translate('interactions.form.loadError'));
    }
  }

  toggleMenu(): void {
    this.showMenu.update((v) => !v);
  }

  closeMenu(): void {
    this.showMenu.set(false);
  }

  editAddress(): void {
    this.router.navigate(['/map/addresses', this.addressId, 'edit']);
  }

  /** Tile selection navigates to a sibling route instead of toggling local
   *  state -- 'objection' and 'lead' are real child/sibling routes now (see
   *  app.routes.ts), not a signal this component's own template branches on.
   *  'no_answer' has no screen of its own: it saves immediately.
   *
   *  Absolute, not relative: relative '..' navigation from this component's
   *  empty-path leaf pops a raw URL segment rather than stepping out to the
   *  parent's logical position, which silently drops the :id -- confirmed via
   *  a Playwright repro during this refactor. Absolute paths sidestep it. */
  private onTileSelected(): void {
    const outcome = this.tileModel.outcome_kind;
    if (outcome === 'no_answer') {
      this.saveNoAnswer();
    } else if (outcome === 'objection') {
      this.router.navigate(['/map/addresses', this.addressId, 'interactions', this.interactionSegment(), 'objection']);
    }
  }

  goToLeadWizard(): void {
    this.router.navigate(['/map/addresses', this.addressId, 'interactions', this.interactionSegment(), 'lead']);
  }

  back(): void {
    this.router.navigate(['/map/addresses', this.addressId, 'interactions', this.interactionSegment()]);
  }

  toggleNoteView(): void {
    this.noteViewOpen.update((v) => !v);
  }

  /** This route's own :interactionId segment -- 'new' when creating. */
  private interactionSegment(): string {
    return this.editingInteractionId ?? 'new';
  }

  confirmDncGate(): void {
    this.dncGateConfirmed = true;
    this.pendingDncConfirm.set(false);
  }

  cancelDncGate(): void {
    this.pendingDncConfirm.set(false);
    this.router.navigate(['/map/addresses', this.addressId]);
  }

  /** ActionSheetHost -- don't let an outside click or drag-to-dismiss interrupt a save in flight. */
  canDismissActionSheet(): boolean {
    return !this.saving();
  }

  private onDncToggle(): void {
    if (!this.dncInitialized) return;
    const address = this.address();
    if (!address) return;
    if (this.dncModel.dnc === !!address.do_not_contact) return;

    if (this.dncModel.dnc) {
      // Lock the sheet immediately -- other actions (outcome tiles, Lead) must
      // stop being tappable the instant the flag is set, not once the write
      // resolves. The DB write itself still happens in the background.
      this.dncFlaggedClosing.set(true);
      setTimeout(() => this.router.navigate(['/map']), 1200);
      this.addressService
        .setDoNotContact(this.addressId, '')
        .catch((err) => this.log.error('Failed to flag do-not-contact', err));
    } else {
      this.addressService.clearDoNotContact(this.addressId).catch((err) => this.log.error('Failed to clear do-not-contact', err));
    }
  }

  /** 'No Answer' resolves immediately -- there's nothing else to gather or
   *  display, so unlike Objection/Lead it never shows a screen of its own. */
  private async saveNoAnswer(): Promise<void> {
    if (this.saving()) return;
    this.saving.set(true);
    this.error.set(null);
    try {
      const overrideDnc = !!this.address()?.do_not_contact;
      const result = await this.interactionService.create({
        address_id: this.addressId,
        interaction_type: this.interactionType,
        outcome_kind: 'no_answer',
        objection_id: '',
        notes: '',
        follow_up_at: null,
        override_do_not_contact: overrideDnc,
      });
      if (!result.success) {
        this.error.set(this.translate.translate(`interactions.form.error.${result.reason}`));
        return;
      }
      if (this.tagIds.length > 0) {
        await this.tagService.saveFor(result.interaction.id, this.tagIds);
      }
      this.router.navigate(['/map']);
    } catch (err: unknown) {
      this.log.error('Failed to save interaction', err);
      this.error.set(err instanceof Error ? err.message : this.translate.translate('interactions.record.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  async save(): Promise<void> {
    if (!this.canSave() || this.saving()) return;
    this.saving.set(true);
    this.error.set(null);

    try {
      const outcome = this.currentOutcome();
      if (outcome !== 'no_answer' && outcome !== 'objection') return;

      const overrideDnc = !!this.address()?.do_not_contact;
      const followUpAt = outcome === 'objection' ? this.fromLocalInput(this.detailModel.follow_up_at) : null;
      const notes = this.noteModel.notes;
      const objectionId = outcome === 'objection' ? this.detailModel.objection_id : '';

      if (this.isEditMode() && this.editingInteractionId) {
        await this.interactionService.update({
          id: this.editingInteractionId,
          outcome_kind: outcome,
          objection_id: objectionId,
          notes,
          follow_up_at: followUpAt,
        });
      } else {
        const result = await this.interactionService.create({
          address_id: this.addressId,
          interaction_type: this.interactionType,
          outcome_kind: outcome,
          objection_id: objectionId,
          notes,
          follow_up_at: followUpAt,
          override_do_not_contact: overrideDnc,
        });
        if (!result.success) {
          this.error.set(this.translate.translate(`interactions.form.error.${result.reason}`));
          return;
        }
        if (this.tagIds.length > 0) {
          await this.tagService.saveFor(result.interaction.id, this.tagIds);
        }
      }

      this.saved.set(true);
      setTimeout(() => this.router.navigate(['/map']), 1200);
    } catch (err: unknown) {
      this.log.error('Failed to save interaction', err);
      this.error.set(err instanceof Error ? err.message : this.translate.translate('interactions.record.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  async saveLegacy(): Promise<void> {
    if (!this.editingInteractionId || this.saving()) return;
    this.saving.set(true);
    this.error.set(null);
    try {
      await this.interactionService.update({ id: this.editingInteractionId, notes: this.legacyModel.notes });
      this.router.navigate(['/map']);
    } catch (err: unknown) {
      this.log.error('Failed to save interaction', err);
      this.error.set(err instanceof Error ? err.message : this.translate.translate('interactions.record.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  /** epoch ms → "YYYY-MM-DDTHH:mm" in local time, for a datetime-local input. */
  private toLocalInput(ms: number | null): string {
    if (!ms) return '';
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private fromLocalInput(value: string): number | null {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
}
